import z from 'zod';
import { DeepPartial, Draft } from '../../common';
import { IAddress, getAddressSchema } from '../../address';
import { IFieldErrors, ValidationError } from '../../validation';
import { IContact, getSchema as getContactSchema } from '../common/contactSchema';
import { diff } from 'deep-object-diff';
import { cleanObject } from 'src/utils';

export interface IDI {
  diCode?: string;
  diMergedCode?: string;
  satEligibilityInd?: boolean;
  satContact?: IContact;
  apEligibilityInd?: boolean;
  apAddress?: Partial<IAddress>;
  apContact?: IContact;
  // clepEligibilityInd?: boolean;
  // clepContact?: IContact;
  // clepAddress?: IAddress;
}

/**
 * Validation Schema Generator
 *
 * This is a common piece of export from a standard schema file.  This function operates as
 * a protected method that should only be used to build parent or child schemas in combination
 * with other standard validation.  It should never be exposed to service logic.  The other
 * method `validate()` should be the expected interface when used in business logic.
 * This helps isolate the scope of impact of the underlying validation library.  None of our
 * service logic should change in the event we swap the validation library.
 */
export const getSchema = (update: Draft<IDI>, type: string, initial?: DeepPartial<IDI>) => {
  const {
    apEligibilityInd,
    satEligibilityInd,
    apAddress,
    //clepEligibilityInd, clepAddress
  } = update;
  const apAddressSchema = getAddressSchema(apAddress?.countryIsoCd);
  // const clepAddressSchema = getAddressSchema(clepAddress?.countryIsoCd);

  //TODO: Check title, name and email
  // const changes: IDI = diff(cleanObject(initial), cleanObject(update));
  // let satPhoneRequired = !!changes?.satContact?.phone;
  // let apPhoneRequired = !!changes?.apContact?.phone;

  const baseDi = z.object({
    diCode: type === 'update' ? z.string().min(4).max(5) : z.undefined(),
    diMergedCode: type === 'update' ? z.string().optional() : z.undefined(),
    apEligibilityInd: z.boolean(),
    apContact: apEligibilityInd
    ? getContactSchema(apEligibilityInd)
    : getContactSchema(apEligibilityInd).optional(),
    // apEligibilityInd
    //     ? getContactSchema(apEligibilityInd, apPhoneRequired)
    //     : getContactSchema(apEligibilityInd, apPhoneRequired).optional(),
    apAddress: apAddress?.countryIsoCd || apAddress?.streetAddr1 ? apAddressSchema : apAddressSchema.optional(),
    satEligibilityInd: z.boolean(),
    satContact: satEligibilityInd
    ? getContactSchema(satEligibilityInd)
    : getContactSchema(satEligibilityInd).optional(),
    // satEligibilityInd
    //     ? getContactSchema(satEligibilityInd, satPhoneRequired)
    //     : getContactSchema(satEligibilityInd, satPhoneRequired).optional(),
    // clepEligibilityInd: z.boolean(),
    // clepContact: getContactSchema(clepEligibilityInd),
    // clepAddress: (clepAddress?.countryIsoCd || clepAddress?.streetAddr1) ? clepAddressSchema : clepAddressSchema.optional()
  });
  return type === 'update'
    ? baseDi
    : baseDi.refine(
        data => data.apEligibilityInd || data.satEligibilityInd, //|| data.clepEligibilityInd
        {
          message: 'At least one program must be selected.',
          path: ['EligibilityInd'],
        },
      );
};

/**
 * Execution of Validation Rules
 *
 * The major expectation from the schema file.  This is the true public export
 * which will commonize the input and output into an application driven abstraction.
 * If this is the only function to reach the service layer, replacing the library will
 * be far more approachable as any logic leaning on this will not have proprietary output
 * in either return type or exception
 */
export const validate = (update: Draft<IDI>, type: string) => {
  const schema = getSchema(update, type);
  try {
    // Ensure there are changes from the original record
    return schema.parse(update);
  } catch (error: any) {
    // console.log('DI ZOD Error : ',  error);
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};
